import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getResults, loadQuestions } from "../lib/api";
import type { CohortFilters, Question, QuestionsPayload, ResultsMap } from "../lib/types";
import CohortFiltersComp from "../components/CohortFilters";

export default function Results() {
  const [data, setData] = useState<QuestionsPayload | null>(null);
  const [results, setResults] = useState<ResultsMap>({});
  const [filters, setFilters] = useState<CohortFilters>({});
  const [cohortFilterApplied, setCohortFilterApplied] = useState(false);
  const [minBucket, setMinBucket] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    void loadQuestions().then(setData);
  }, []);

  useEffect(() => {
    setLoading(true);
    getResults({ filters })
      .then((r) => {
        setResults(r.results);
        setCohortFilterApplied(r.cohortFilterApplied);
        setMinBucket(r.minBucket);
      })
      .finally(() => setLoading(false));
  }, [filters]);

  if (!data) return <p className="text-muted">Loading…</p>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-8">
      <div className="space-y-10">
        <header>
          <h1 className="text-3xl font-semibold">Results</h1>
          <p className="mt-2 text-ink/80 max-w-prose">
            Live aggregated results across all 100 questions. Anonymous, cohort-filterable.
          </p>
          {cohortFilterApplied && minBucket && (
            <p className="mt-2 text-sm text-muted">
              Cohort filter applied · breakdowns suppressed for questions with fewer
              than {minBucket} votes.
            </p>
          )}
        </header>
        {loading && <p className="text-sm text-muted">Recalculating…</p>}
        {data.themes.map((theme) => {
          const themeQuestions = data.questions.filter((q) => q.themeId === theme.id);
          return (
            <section key={theme.id}>
              <h2 className="text-xl font-semibold">
                Theme {theme.id} — {theme.title}
              </h2>
              <div className="mt-3 space-y-3">
                {themeQuestions.map((q) => (
                  <QuestionResultsRow
                    key={q.id}
                    question={q}
                    result={results[q.id]}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>
      <aside className="lg:sticky lg:top-20 lg:self-start space-y-4">
        <CohortFiltersComp value={filters} onChange={setFilters} />
      </aside>
    </div>
  );
}

function QuestionResultsRow({
  question,
  result,
}: {
  question: Question;
  result?: { total: number; options: Record<string, number> };
}) {
  const total = result?.total ?? 0;
  const counts = result?.options ?? {};

  return (
    <Link to={`/questions/${question.id}`} className="card no-underline block hover:border-cedar transition">
      <div className="flex items-baseline justify-between mb-2">
        <div className="flex items-baseline gap-3">
          <span className="font-mono text-xs text-muted">{question.id}</span>
          <span className="text-sm font-medium text-ink">{question.title}</span>
        </div>
        <span className="text-xs text-muted tabular-nums">
          {total} {total === 1 ? "vote" : "votes"}
        </span>
      </div>
      {total === 0 ? (
        <p className="text-xs text-muted">No votes yet.</p>
      ) : Object.keys(counts).length === 0 ? (
        <p className="text-xs text-muted">Breakdown suppressed (below privacy threshold).</p>
      ) : (
        <ul className="space-y-1">
          {question.options.map((opt) => {
            const n = counts[opt.key] ?? 0;
            const pct = total ? Math.round((n / total) * 100) : 0;
            return (
              <li key={opt.key} className="flex items-baseline gap-2 text-xs">
                <span className="font-mono w-4">{opt.key}</span>
                <span className="flex-1 h-1.5 bg-sand rounded relative overflow-hidden">
                  <span
                    className="absolute inset-y-0 left-0 bg-cedar"
                    style={{ width: `${pct}%` }}
                  />
                </span>
                <span className="tabular-nums w-12 text-right text-muted">
                  {pct}%
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </Link>
  );
}
